(() => {
  if (customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-product-recommendations-content]');

      if (!this.content) {
        this.hide();
        return;
      }

      if (this.hasProducts()) {
        this.dataset.state = 'loaded';
        this.dataset.hasProducts = 'true';
        this.hidden = false;
        this.revealCards();
        return;
      }

      if (this.dataset.state === 'empty' || this.dataset.state === 'error') {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.dataset.state = 'idle';
      this.observe();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();

      if (this.dataset.state === 'loading') {
        this.dataset.state = 'idle';
      }
    }

    observe() {
      if (!this.dataset.url) {
        this.hide();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer?.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '600px 0px', threshold: 0.01 }
      );
      this.observer.observe(this);
    }

    async load() {
      if (this.dataset.state === 'loading' || this.dataset.state === 'loaded') return;

      this.dataset.state = 'loading';
      this.controller = new AbortController();

      try {
        const response = await fetch(this.dataset.url, {
          credentials: 'same-origin',
          signal: this.controller.signal
        });

        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingRoot = documentFragment.querySelector('[data-product-recommendations-root]');
        const incomingContent = incomingRoot?.querySelector('[data-product-recommendations-content]');

        if (!incomingContent?.querySelector('.product-card')) {
          this.hide();
          return;
        }

        this.content.innerHTML = incomingContent.innerHTML;
        this.dataset.state = 'loaded';
        this.dataset.hasProducts = 'true';
        this.hidden = false;
        this.revealCards();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.dataset.state = 'error';
        this.hide(false);
      } finally {
        this.controller = null;
      }
    }

    hasProducts() {
      return Boolean(this.content?.querySelector('.product-card'));
    }

    revealCards() {
      requestAnimationFrame(() => {
        this.querySelectorAll('.reveal').forEach((card) => card.classList.add('is-visible'));
      });
    }

    hide(markEmpty = true) {
      this.observer?.disconnect();
      this.dataset.hasProducts = 'false';
      this.dataset.state = markEmpty ? 'empty' : this.dataset.state;
      this.hidden = true;
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
